// DrawerRail — side navigation of InputDrawerV3.
// Renders grouped tab buttons; items carry { id, label, icon, badge?, group }.

import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import useDrawerStyles from "./styles";
import useThemeColors from "../../../../hooks/useThemeColors";

const GROUP_TITLE_KEYS = {
  trail: "modeling_inputs_v3.rail.group_trail",
  about_input: "modeling_inputs_v3.rail.group_about_input",
  config: "modeling_inputs_v3.rail.group_config",
  adm: "modeling_inputs_v3.rail.group_adm",
};

const DrawerRail = ({ items, activeId, onSelect }) => {
  const { clientColor } = useThemeColors();
  const classes = useDrawerStyles({ clientColor });
  const { t } = useTranslation();

  const grouped = items.reduce((acc, item) => {
    const g = item.group || "now";
    if (!acc[g]) acc[g] = [];
    acc[g].push(item);
    return acc;
  }, {});

  return (
    <nav
      className={classes.rail}
      aria-label={t("modeling_inputs_v3.rail.aria_nav")}
    >
      {Object.keys(GROUP_TITLE_KEYS).map((groupKey) => {
        const groupItems = grouped[groupKey];
        if (!groupItems || groupItems.length === 0) return null;
        return (
          <div key={groupKey} className={classes.railGroup}>
            <div className={classes.railGroupLabel}>
              {t(GROUP_TITLE_KEYS[groupKey])}
            </div>
            {groupItems.map((item) => {
              const active = item.id === activeId;
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onSelect(item.id)}
                  className={`${classes.railItem} ${
                    active ? classes.railItemActive : ""
                  }`}
                >
                  {Icon && <Icon />}
                  <span>{item.label}</span>
                  {item.badgeDot && (
                    <span
                      className={`${classes.dotBadge} ${
                        item.badgeDot === "critical"
                          ? classes.dotBadgeDanger
                          : classes.dotBadgeWarning
                      }`}
                    />
                  )}
                  {typeof item.badgeCount === "number" &&
                    item.badgeCount > 0 && (
                      <span className={classes.railCount}>
                        {item.badgeCount}
                      </span>
                    )}
                </button>
              );
            })}
          </div>
        );
      })}
    </nav>
  );
};

DrawerRail.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.elementType,
      group: PropTypes.oneOf([
        "now",
        "context",
        "trail",
        "about_input",
        "config",
        "adm",
      ]),
      badgeDot: PropTypes.oneOf(["critical", "warning"]),
      badgeCount: PropTypes.number,
    }),
  ).isRequired,
  activeId: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default DrawerRail;
